/**
 * Minimal ReadingText
 * - Sets --reading-text-scroll-height = reading-speed * number-of-chars (vh)
 * - Letter reveal is a single ScrollTrigger timeline:
 *     start: "center center"  (when section's center hits viewport center)
 *     end:   "bottom center"  (when section's bottom hits viewport center)
 *   => Ensures no progress before middle; finishes reveal as sticky ends.
 */

class ReadingText extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = new ResizeObserver(() => this.refreshLayout());
    this._st = null;   // ScrollTrigger instance
    this._tl = null;   // GSAP timeline (unused in manual mode)
    this._letterTriggers = []; // per-letter ScrollTriggers (not used now)
    this._enterTriggers = []; // enter animations triggers
  }

  connectedCallback() {
    this.resizeObserver.observe(this);
    const header = document.querySelector('[data-section-type="header"]');
    if (header) this.resizeObserver.observe(header);
    const split = this.splitEl;
    if (split) this.resizeObserver.observe(split);

    // Wait until split-lines is ready, then init
    this.waitForSplitLines(() => {
      this.primeCharsOpacity();
      this.setScrollHeight();
      this.buildScrollTimeline();
      this.setupEnterAnimations();
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    });

    // Keep things in sync on resize (viewport height affects the sticky & centers)
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    if (this._st) this._st.kill();
    if (this._tl) this._tl.kill();
    this._letterTriggers.forEach(tr => tr.kill());
    this._letterTriggers = [];
    this._enterTriggers?.forEach(t => t.kill());
    this._enterTriggers = [];
    this._enterStates?.forEach?.((state) => state.tl?.kill?.());
    this._enterStates = new WeakMap();
  }

  // ----- DOM shortcuts -----
  get splitEl() { return this.querySelector('split-lines'); }
  get lines() { return this.splitEl?.lines || []; }
  get chars() { return this.lines.flatMap(line => Array.from(line.children)); }
  get textStartOpacity() { return parseFloat(this.getAttribute('text-start-opacity')) || 0.25; }
  get readingSpeed() { return parseFloat(this.getAttribute('reading-speed')) || 1; }

  // ----- lifecycle helpers -----
  waitForSplitLines(done) {
    const el = this.splitEl;
    if (!el) {
      // no splitter—still run (fallback)
      requestAnimationFrame(done);
      return;
    }
    if (el.classList.contains('is-split')) {
      requestAnimationFrame(done);
      return;
    }
    el.addEventListener('split-lines:split', () => requestAnimationFrame(done), { once: true });
  }

  // ----- bottom-enter staggered animation for children -----
  setupEnterAnimations() {
    const containers = this.querySelectorAll('[data-enter-animation]');
    if (!containers.length || !window.gsap) return;
  
    gsap.registerPlugin(ScrollTrigger);
    this._enterTriggers ||= [];
    this._enterStates ||= new WeakMap();
  
    containers.forEach((container) => {
      const state = this._enterStates.get(container) || { tl: null, st: null, exitedBottom: false };
  
      const getItems = () => {
        const split = container.querySelector('split-lines');
        return Array.isArray(split?.lines) ? split.lines : [];
      };
  
      const killTimeline = () => {
        if (state.tl) { state.tl.kill(); state.tl = null; }
        gsap.killTweensOf(getItems());
      };
  
      const reset = () => {
        const items = getItems();
        if (items.length) gsap.set(items, { y: 24, opacity: 0, willChange: 'transform' });
      };
  
      const resolve = () => {
        const items = getItems();
        if (items.length) gsap.set(items, { y: 0, opacity: 1, clearProps: 'willChange' });
        // no running TL needed when resolved
        killTimeline();
      };
  
      const buildTimeline = () => {
        const items = getItems();
        if (!items.length) return;
        killTimeline();
        gsap.set(items, { y: 24, opacity: 0, willChange: 'transform' });
        state.tl = gsap.timeline({ paused: true, defaults: { ease: 'power1.out' } })
          .to(items, {
            y: 0,
            opacity: 1,
            duration: 0.25,
            stagger: 0.08,
            overwrite: 'auto',
            immediateRender: false
          }, 0);
      };
  
      const isInViewportNow = () => {
        const r = container.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        return r.bottom > 0 && r.top < vh;
      };
  
      const isAboveViewportNow = () => {
        const r = container.getBoundingClientRect();
        // small epsilon to avoid 0px rounding jitter
        return r.bottom <= 1;
      };
  
      // Initial baseline
      reset();
  
      state.st = ScrollTrigger.create({
        trigger: container,
        start: 'top bottom',
        end: 'bottom top',
        invalidateOnRefresh: true,
  
        onEnter: () => {
          state.exitedBottom = false;
          if (!state.tl) buildTimeline();
          state.tl && state.tl.play(0);
        },
  
        // Only restart when it had fully left via bottom previously
        onEnterBack: () => {
          if (!state.exitedBottom) return;
          if (!state.tl) buildTimeline();
          state.tl && state.tl.play(0);
        },
  
        // Scrolled past top -> keep fully resolved
        onLeave: () => {
          state.tl && state.tl.progress(1).pause();
        },
  
        // Left via bottom (scrolling up) -> reset
        onLeaveBack: () => {
          state.exitedBottom = true;
          killTimeline();
          reset();
        },
  
        onRefresh: () => {
          state.exitedBottom = false;
          // If it’s above the viewport after refresh, keep it resolved.
          if (isAboveViewportNow()) {
            resolve();
            return;
          }
          // Visible now? build & play. Otherwise reset.
          killTimeline();
          if (state.st?.isActive || isInViewportNow()) {
            buildTimeline();
            state.tl && state.tl.play(0);
          } else {
            reset();
          }
        },
      });
  
      // 🔸 First paint kick: handle above/in-view cases
      requestAnimationFrame(() => {
        state.st.refresh?.();
        if (isAboveViewportNow()) {
          resolve();
        } else if (state.st.isActive || isInViewportNow()) {
          if (!state.tl) buildTimeline();
          state.tl && state.tl.play(0);
        } else {
          reset();
        }
      });
  
      // If split-lines re-splits later, apply same logic
      container.querySelector('split-lines')?.addEventListener('split-lines:split', () => {
        state.exitedBottom = false;
        if (isAboveViewportNow()) {
          resolve();
        } else if (state.st?.isActive || isInViewportNow()) {
          killTimeline();
          buildTimeline();
          state.tl && state.tl.play(0);
        } else {
          killTimeline();
          reset();
        }
      });
  
      this._enterStates.set(container, state);
      this._enterTriggers.push(state.st);
    });
  }

  primeCharsOpacity() {
    const start = this.textStartOpacity;
    if (window.gsap) {
      this.chars.forEach(ch => gsap.set(ch, { opacity: start }));
      const btn = this.querySelector('.button');
      if (btn) gsap.set(btn, { opacity: start });
    } else {
      this.chars.forEach(ch => { ch.style.opacity = String(start); });
      const btn = this.querySelector('.button');
      if (btn) btn.style.opacity = String(start);
    }
  }

  setScrollHeight() {
    // readingSpeed in vh per character
    const h = this.readingSpeed * this.chars.length;
    this.style.setProperty('--reading-text-scroll-height', `${h}vh`);
  }

  refreshLayout = () => {
    // Force split-lines to reflow on resize so lines map matches new layout
    const split = this.splitEl;
    if (split && typeof split.split === 'function') {
      try { split.split(true); } catch (e) {}
    }
    this.setScrollHeight();
    // Rebuild the ScrollTrigger because start/end depend on viewport & header height
    if (this._st) this._st.kill();
    if (this._tl) this._tl.kill();
    this.buildScrollTimeline();
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  };

  handleResize = () => {
    // viewport height changed; rebuild start/end math & refresh
    this.refreshLayout();
  };

  // ----- scroll-driven letters -----
  buildScrollTimeline() {
    if (!window.gsap) return; // requires GSAP
    if (!this.chars.length) return;

    // Kill previous triggers
    this._letterTriggers.forEach(tr => tr.kill());
    this._letterTriggers = [];
    if (this._st) { this._st.kill(); this._st = null; }
    if (this._tl) { this._tl.kill(); this._tl = null; }

    // --- DOM references ---
    const sectionEl = this; // <reading-text>
    const contentEl = sectionEl.querySelector('[data-reading-text-content]') || sectionEl;
    const stickyEl = sectionEl.querySelector('.reading-text__inner') || contentEl;

    // --- Read header height from CSS var (falls back to 0) ---
    const root = document.documentElement;
    const headerVar = getComputedStyle(root).getPropertyValue('--f-header-height') || '0px';
    const headerHeight = parseFloat(headerVar) || 0;

    // Sticky scroll distance ≈ section height - sticky height (matches CSS math)
    const stickyDistance = Math.max(0, sectionEl.offsetHeight - stickyEl.offsetHeight);

    // Prime opacity (defensive)
    const startOpacity = this.textStartOpacity;
    this.chars.forEach(ch => gsap.set(ch, { opacity: startOpacity }));
    const btn = sectionEl.querySelector('.button');
    if (btn) gsap.set(btn, { opacity: startOpacity });

    gsap.registerPlugin(ScrollTrigger);

    const total = this.chars.length;
    const revealWindow = 15; // number of letters across which each fade spans

    // Single scrubbed ScrollTrigger that maps progress -> per-letter opacity
    this._st = ScrollTrigger.create({
      trigger: stickyEl,
      start: () => `top top+=${headerHeight}`,
      end: () => `+=${stickyDistance}`,
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const p = self.progress; // 0..1
        const cutoff = p * (total + revealWindow);
        // Letters
        for (let i = 0; i < total; i++) {
          const t = Math.max(0, Math.min(1, (cutoff - i) / (revealWindow + 1)));
          const op = startOpacity + t * (1 - startOpacity);
          gsap.set(this.chars[i], { opacity: op });
        }
        // Button treated as i = total
        if (btn) {
          const tBtn = Math.max(0, Math.min(1, (cutoff - total) / (revealWindow + 1)));
          const opBtn = startOpacity + tBtn * (1 - startOpacity);
          gsap.set(btn, { opacity: opBtn });
        }
      },
      onLeaveBack: () => {
        this.chars.forEach(ch => gsap.set(ch, { opacity: startOpacity }));
        if (btn) gsap.set(btn, { opacity: startOpacity });
      },
      onRefresh: () => {
        this.chars.forEach(ch => gsap.set(ch, { opacity: startOpacity }));
        if (btn) gsap.set(btn, { opacity: startOpacity });
      },
      // markers: true,
    });
  }
}

// Register the custom element
if (!window.customElements.get('reading-text')) {
  window.customElements.define('reading-text', ReadingText);
}
